import React, { useRef } from "react";
import { useNavigate, useLocation, Routes, Route, Navigate } from "react-router-dom";
import { BrowserRouter } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Toaster } from "@/components/ui/sonner";
import Layout from "@/components/Layout";
import LandingPage from "@/pages/LandingPage";
import AuthPage from "@/pages/AuthPage";
import AuthCallback from "@/pages/AuthCallback";
import OnboardingPage from "@/pages/OnboardingPage";
import DashboardPage from "@/pages/DashboardPage";
import VaultPage from "@/pages/VaultPage";
import JournalPage from "@/pages/JournalPage";
import VoiceChatPage from "@/pages/VoiceChatPage";
import DailyPromptsPage from "@/pages/DailyPromptsPage";
import LegacyMessagesPage from "@/pages/LegacyMessagesPage";
import BeneficiaryPage from "@/pages/BeneficiaryPage";
import ProfilePage from "@/pages/ProfilePage";
import TimelinePage from "@/pages/TimelinePage";
import FacebookConnectPage from "@/pages/FacebookConnectPage";
import ClaimPage from "@/pages/ClaimPage";
import AdminPage from "@/pages/AdminPage";
import SuperAdminPage from "@/pages/SuperAdminPage";
import ConnectionsPage from "@/pages/ConnectionsPage";
import SpotifyPage from "@/pages/SpotifyPage";
import DataImportPage from "@/pages/DataImportPage";
import DigitalDNAPage from "@/pages/DigitalDNAPage";
import KnowledgeBasePage from "@/pages/KnowledgeBasePage";
import CommunityFeedPage from "@/pages/CommunityFeedPage";
import LeaderboardPage from "@/pages/LeaderboardPage";
import MealTrackerPage from "@/pages/MealTrackerPage";
import PointsPage from "@/pages/PointsPage";
import SearchPage from "@/pages/SearchPage";
import OuraCallbackPage from "@/pages/OuraCallbackPage";
import OuraPage from "@/pages/OuraPage";

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="h-screen flex items-center justify-center bg-[#020202]">
        <div className="w-8 h-8 border-2 border-[#00FF88] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!user) return <Navigate to="/auth" replace />;

  if (!user.onboarding_complete && window.location.pathname !== "/onboarding") {
    return <Navigate to="/onboarding" replace />;
  }

  return <Layout>{children}</Layout>;
}

function AppRouter() {
  const location = useLocation();

  // Check URL fragment for session_id (synchronous - before render)
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/auth" element={<AuthPage />} />
      <Route path="/claim" element={<ClaimPage />} />
      <Route path="/onboarding" element={<OnboardingWrapper />} />
      <Route path="/dashboard" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
      <Route path="/vault" element={<ProtectedRoute><VaultPage /></ProtectedRoute>} />
      <Route path="/journal" element={<ProtectedRoute><JournalPage /></ProtectedRoute>} />
      <Route path="/journal/:entryId" element={<ProtectedRoute><JournalPage /></ProtectedRoute>} />
      <Route path="/voice-chat" element={<ProtectedRoute><VoiceChatPage /></ProtectedRoute>} />
      <Route path="/prompts" element={<ProtectedRoute><DailyPromptsPage /></ProtectedRoute>} />
      <Route path="/legacy" element={<ProtectedRoute><LegacyMessagesPage /></ProtectedRoute>} />
      <Route path="/beneficiary" element={<ProtectedRoute><BeneficiaryPage /></ProtectedRoute>} />
      <Route path="/timeline" element={<ProtectedRoute><TimelinePage /></ProtectedRoute>} />
      <Route path="/facebook" element={<ProtectedRoute><FacebookConnectPage /></ProtectedRoute>} />
      <Route path="/connections" element={<ProtectedRoute><ConnectionsPage /></ProtectedRoute>} />
      <Route path="/connections/spotify" element={<ProtectedRoute><SpotifyPage /></ProtectedRoute>} />
      <Route path="/connections/spotify/callback" element={<ProtectedRoute><SpotifyPage /></ProtectedRoute>} />
      <Route path="/connections/oura" element={<ProtectedRoute><OuraPage /></ProtectedRoute>} />
      <Route path="/connections/oura/callback" element={<ProtectedRoute><OuraCallbackPage /></ProtectedRoute>} />
      <Route path="/connections/import" element={<ProtectedRoute><DataImportPage /></ProtectedRoute>} />
      <Route path="/digital-dna" element={<ProtectedRoute><DigitalDNAPage /></ProtectedRoute>} />
      <Route path="/knowledge-base" element={<ProtectedRoute><KnowledgeBasePage /></ProtectedRoute>} />
      <Route path="/community" element={<ProtectedRoute><CommunityFeedPage /></ProtectedRoute>} />
      <Route path="/leaderboard" element={<ProtectedRoute><LeaderboardPage /></ProtectedRoute>} />
      <Route path="/meals" element={<ProtectedRoute><MealTrackerPage /></ProtectedRoute>} />
      <Route path="/points" element={<ProtectedRoute><PointsPage /></ProtectedRoute>} />
      <Route path="/search" element={<ProtectedRoute><SearchPage /></ProtectedRoute>} />
      <Route path="/admin" element={<ProtectedRoute><AdminPage /></ProtectedRoute>} />
      <Route path="/superadmin" element={<ProtectedRoute><SuperAdminPage /></ProtectedRoute>} />
      <Route path="/profile" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function OnboardingWrapper() {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="h-screen flex items-center justify-center bg-[#020202]">
        <div className="w-8 h-8 border-2 border-[#00FF88] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  if (!user) return <Navigate to="/auth" replace />;
  if (user.onboarding_complete) return <Navigate to="/dashboard" replace />;
  return <OnboardingPage />;
}

function App() {
  return (
    <div className="noise-overlay">
      <BrowserRouter>
        <AuthProvider>
          <AppRouter />
          <Toaster
            position="top-right"
            toastOptions={{
              style: { background: '#0A0A0A', border: '1px solid rgba(255,255,255,0.08)', color: '#F8FAFC' }
            }}
          />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
